/* eslint-disable max-len */
import { Box, Grid, Stack } from '@chakra-ui/react';
import ArticleHeading from '@components/articles/ArticleHeading';
import TextSection from '@components/articles/SeoTextSection';
import TextLink from '@components/interactive/TextLink';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import { CTAOptions, OmniBox } from '@components/omniBox';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import { PhoneNumbers } from '@constants/phoneNumbers';
import { Routes } from '@constants/routes';
import { antlerStyle } from '@constants/styles';
import { encodeSlug } from '@utils/slugs';
import { useRouter } from 'next/router';
import { titleCase } from 'title-case';
import PlanF from './PlanF';
import PlanG from './PlanG';
import PlanN from './PlanN';

const statesOutlawsExcessCharges = [
  'Connecticut',
  'Massachusetts',
  'Minnesota',
  'New York',
  'Ohio',
  'Pennsylvania',
  'Rhode Island',
  'Vermont',
];

const BestMedSupInRegion = ({ data }) => {
  const router = useRouter();

  const name = titleCase(data.name);
  const offersN = !!data.planNCarriers.length;
  const recommendN = offersN && statesOutlawsExcessCharges.includes(titleCase(data.state));

  const stateBlurb = data.blurb.split('. ');

  const renderCta = () => (
    <OmniBox
      cta={CTAOptions.MEDICARE_ADVOCATE}
      phoneNumber={router.asPath.includes('?') ? PhoneNumbers.GOOGLE : PhoneNumbers.MS_SEO}
    />
  );

  return (
    <article>
      <DefaultLayout>
        <Stack w="100%" px={antlerStyle.px} alignItems="center" py={[12, 12, 24, 24]} bg="tan">
          <Stack spacing={12} mx={[0, 0, 0, 24]} maxW="840px" width="100%">
            <TextSection>
              <header>
                <ArticleHeading
                  imageSrc={data.image}
                  imageAlt={data.alt}
                  title={data.title}
                  dateString={data.publishDate}
                  backHref={Routes.bestMedicareSupplement}
                />
              </header>
              <Body1>{stateBlurb.slice(0, 3).join('. ')}.</Body1>
              {renderCta()}
              <Body1>{stateBlurb.slice(3, stateBlurb.length).join('. ')}</Body1>
              <Body1>
                <i>
                  The prices below are estimates and will vary according to your age, sex, lifestyle, and location.
                  Contact us to get personalized quotes!
                </i>
              </Body1>
            </TextSection>
            <TextSection title={`In ${name}, the Best Choice is ${recommendN ? 'G or N' : 'G'}.`}>
              <Body1>
                Without context, choosing between Medicare Supplements is like choosing letters from an alphabet soup.
                In {name}, there is Plan A, Plan B, Plan C, Plan D, Plan F, Plan G, Plan K, Plan M, and Plan N.
                Fortunately, there's an easy heuristic to decide between them:
              </Body1>
              <Body1>
                All Medicare Supplements with the same plan letter must, by law, offer the same coverage (regardless of
                carrier or region). This makes them easily comparable. In {name}, plans A, B, C, D, K,
                {recommendN ? ' and M' : ' M, and N'} can all be thrown out from consideration because they offer less
                coverage, and usually cost more than the optimal Plan G {recommendN && 'or N'}. Plan F is a unique
                exception here, which we address below.
              </Body1>
              <Body1>
                <b>Takeaway:</b> Plan G {recommendN && ' or N'} offer{!recommendN && 's'} the most comprehensive
                coverage for your dollar.
              </Body1>
            </TextSection>
            <TextSection title="Why Medicare Supplements over Medicare Advantage Plans?">
              <Body1>
                Unlike Medicare Advantage plans, you will be <b>covered by any health provider that accepts Medicare</b>{' '}
                under a Supplement Plan (about 90% of doctors nationwide). So you don't need to worry about a Carrier
                network, or getting prior approval for a procedure. The extent of that coverage is the other
                consideration, and no other Medicare plan covers out-of-pocket expenses as comprehensively as Plan G
                {recommendN && ' or N'}.
              </Body1>
            </TextSection>
            {!!data.planGCarriers.length && (
              <PlanG
                name={name}
                carriers={data.planGCarriers}
                averagePremium={data.planGAverage}
                renderCta={renderCta}
              />
            )}
            {recommendN && (
              <PlanN
                name={name}
                carriers={data.planNCarriers}
                averagePremium={data.planNAverage}
                renderCta={renderCta}
              />
            )}
            {/* Always render Plan F information,
             *  but conditionally render sub info
             *  (i.e. average price / cheapest carriers)
             */}
            <PlanF name={name} carriers={data.planFCarriers} averagePremium={data.planFAverage} renderCta={renderCta} />
            {data.cityList && data.cityList.length > 0 && (
              <Stack py="40px" spacing={4} alignItems="center">
                <Header as="h4">Cities in {data.name}</Header>
                <Grid templateColumns={['repeat(2, 1fr)', 'repeat(2, 1fr)', 'repeat(4, 1fr)', 'repeat(5, 1fr)']} pt={8}>
                  {data.cityList.map((city) => (
                    <Box justifyContent="center" display="flex" px={4} py={2} key={encodeSlug(city)}>
                      <TextLink
                        text={titleCase(city)}
                        href={`${Routes.bestMedicareSupplement}/${encodeSlug(data.name)}/${encodeSlug(city)}`}
                        whiteSpace="nowrap"
                      />
                    </Box>
                  ))}
                </Grid>
              </Stack>
            )}
          </Stack>
        </Stack>
      </DefaultLayout>
    </article>
  );
};

export default BestMedSupInRegion;
